import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { useQuery } from 'react-query';
import axios from 'axios';

import { getUserNoFromToken } from '../../utils/auth';

// Components
import VerticalHealthTimeline from '../../components/dashboard/VerticalHealthTimeline';
import PreCheckPanel from '../../components/dashboard/PreCheckPanel';
import HealthGradeSummary from '../../components/dashboard/HealthGradeSummary';
import HealthTrendChart from '../../components/dashboard/HealthTrendChart';
 

const DashboardContainer = styled.div`
  /* [변경 사유]
   * - 초기 기획/컨셉에 맞춰 "상단(Trend 풀폭) + 하단(좌: 타임라인 / 우: 점검/등급)" 구조로 재배치합니다.
   * - 기존 2열 그리드(동일 위계 카드 나열)는 컨텐츠 특성을 반영하기 어렵고, 상담 흐름(스토리텔링)을 만들기 힘듭니다.
   */
  display: flex;
  flex-direction: column;
  gap: 20px;
  /* [디자인 기준]
   * - 태블릿 사용을 기본으로 하므로 1차 목표는 800px이지만,
   *   2단 레이아웃(좌/우)을 유지하며 컨텐츠 가독성을 확보하려면 800px은 다소 타이트할 수 있어
   *   우선 1024px을 표준으로 적용합니다.
   */
  max-width: 1024px;
  width: 100%;
  margin: 0 auto;
  padding: 0 16px;
  padding-bottom: 40px;

  @media (max-width: 1024px) {
    max-width: 100%;
  }
`;

const Section = styled.div`
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  /* [변경 사유]
   * - FullWidthSection에서 width: 100%를 사용할 때 기본 box-sizing(content-box)이면
   *   padding이 폭에 더해져 상단 Trend 카드가 하단 2단 카드 묶음보다 미세하게 더 넓어 보일 수 있습니다.
   * - border-box로 통일해 카드 박스의 좌우 끝선이 동일선상에 오도록 정렬 문제를 예방합니다.
   */
  box-sizing: border-box;
  /* [변경 사유]
   * - 우측 카드(Screening/Grade)가 내용 대비 과도하게 길어지는 문제를 줄이기 위해
   *   기본 min-height/padding을 축소합니다.
   */
  min-height: 140px;
  /* [변경 사유]
   * - CSS Grid/Flex 컨테이너 안에서 자식 요소가 내용(텍스트/표/칩 등) 때문에
   *   최소 폭을 강제(min-width:auto)하면 브라우저 폭이 줄어도 카드가 함께 줄지 않고
   *   가로 스크롤/잘림이 발생할 수 있습니다.
   * - min-width: 0으로 설정해 카드가 그리드 폭에 맞춰 정상적으로 축소되도록 합니다.
   */
  min-width: 0;
  overflow-x: hidden;
`;

const FullWidthSection = styled(Section)`
  width: 100%;
`;

const BottomRow = styled.div`
  display: grid;
  /* [변경 사유]
   * - 기존 360px 고정 좌측 컬럼은 브라우저 폭이 줄어도 2열 구간에서 함께 줄지 않아
   *   카드가 잘리거나 가로 스크롤이 생길 수 있습니다.
   * - minmax로 좌측 컬럼도 함께 줄어들게 하고,
   *   우측 컬럼은 minmax(0, 1fr)로 설정해 내부 콘텐츠 때문에 폭이 강제 확장되는 것을 방지합니다.
   */
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
  gap: 20px;
  /* [변경 사유]
   * - Timeline은 반복 데이터가 길어질 수 있어 내부 스크롤 영역으로 분리합니다.
   * - 하단 2열에서는 좌측 Timeline 카드 바닥선이 우측 Health Grade 바닥선과 맞아야 하므로
   *   grid item을 stretch로 맞춥니다.
   */
  align-items: stretch;

  /* [반응형]
   * - 800px에서도 2단을 유지할 수 있도록 브레이크포인트를 낮춥니다.
   */
  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  /* [변경 사유]
   * - Grid 아이템의 기본 min-width:auto 특성으로 인해 내용이 긴 경우
   *   컬럼이 줄어들지 못하고 오버플로우가 생길 수 있어 최소 폭을 해제합니다.
   */
  min-width: 0;
  min-height: 0;
`;

const RightColumn = styled.div`
  /* [변경 사유]
   * - 우측 컬럼은 내용 기반으로 쌓되,
   *   하단 grid stretch 기준의 높이 산정 기준이 됩니다.
   */
  display: flex;
  flex-direction: column;
  gap: 20px;
  /* [변경 사유]
   * - 우측(Pre-Check/Health Grade) 내부 요소가 긴 경우에도
   *   브라우저 폭 축소에 맞춰 카드가 함께 줄어들도록 최소 폭을 해제합니다.
   */
  min-width: 0;
  min-height: 0;
`;

const StretchSection = styled(Section)`
  /* [변경 사유]
   * - 공통 섹션은 내용 높이에 따라 자연스럽게 흐르게 둡니다.
   * - 반복 목록처럼 높이 제한이 필요한 영역은 파생 컴포넌트에서만 별도로 처리합니다.
   */
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionBody = styled.div`
  min-width: 0;
  overflow: visible;
  /* [변경 사유]
   * - 공통 본문은 중첩 스크롤을 만들지 않고 섹션 흐름에 맡깁니다.
   * - Timeline처럼 반복 데이터가 긴 영역만 TimelineBody에서 예외적으로 스크롤 처리합니다.
  */
`;

const TimelineSection = styled(StretchSection)`
  height: ${({ $height }) => ($height ? `${$height}px` : 'clamp(560px, 64vh, 760px)')};
  min-height: 0;

  @media (max-width: 860px) {
    height: min(58vh, 560px);
    min-height: 360px;
  }
`;

const TimelineBody = styled(SectionBody)`
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
  padding-right: 6px;
  -webkit-overflow-scrolling: touch;
`;

const BriefingSection = styled(Section)`
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 1.4fr);
  gap: 16px;
  border: 1px solid #bee3f8;
  background: linear-gradient(135deg, #f7fbff 0%, #ffffff 58%, #f0fff4 100%);

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const BriefingLead = styled.div`
  min-width: 0;
`;

const Eyebrow = styled.div`
  font-size: 0.78rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 800;
  color: #2b6cb0;
  margin-bottom: 8px;
`;

const BriefingTitle = styled.h2`
  margin: 0;
  font-size: 1.35rem;
  line-height: 1.25;
  color: #102a43;
`;

const BriefingText = styled.p`
  margin: 10px 0 0;
  color: #486581;
  line-height: 1.55;
`;

const BriefingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const BriefingCard = styled.div`
  border: 1px solid ${props => props.border || '#d9e2ec'};
  border-radius: 14px;
  background: ${props => props.bg || '#ffffff'};
  padding: 12px;
  min-width: 0;
`;

const BriefingCardLabel = styled.div`
  font-size: 0.78rem;
  font-weight: 800;
  color: #52606d;
  margin-bottom: 6px;
`;

const BriefingCardTitle = styled.div`
  font-size: 1rem;
  font-weight: 900;
  color: #102a43;
  line-height: 1.35;
`;

const BriefingCardDesc = styled.div`
  margin-top: 6px;
  font-size: 0.86rem;
  color: #486581;
  line-height: 1.45;
`;

const ActionList = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const ActionCard = styled.div`
  border: 1px solid #d9e2ec;
  border-radius: 14px;
  background: #ffffff;
  padding: 12px;
  min-width: 0;
`;

const ActionTitle = styled.div`
  font-size: 0.92rem;
  font-weight: 900;
  color: #102a43;
`;

const ActionDesc = styled.div`
  margin-top: 6px;
  color: #52606d;
  font-size: 0.86rem;
  line-height: 1.45;
`;

const Title = styled.h2`
  font-size: 1.1rem;
  font-weight: 600;
  color: #1a202c;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LoadingState = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 400px;
    font-size: 1.2rem;
    color: #666;
`;

const ErrorState = styled.div`
    color: #e53e3e;
    text-align: center;
    padding: 20px;
`;

const countTrendLevels = (trendV2) => {
    const indicators = Array.isArray(trendV2?.indicators) ? trendV2.indicators : [];
    return indicators.reduce(
        (acc, indicator) => {
            const points = Array.isArray(indicator?.points) ? indicator.points : [];
            const latest = points[points.length - 1];
            if (latest?.level === 'danger') acc.danger += 1;
            if (latest?.level === 'caution') acc.caution += 1;
            if (latest?.level === 'normal') acc.normal += 1;
            return acc;
        },
        { danger: 0, caution: 0, normal: 0 },
    );
};

const getFiveYearPrecheckCount = (dashboardData) => {
    const v2Items = dashboardData?.disclosure_check_v2?.windows?.['5y']?.items_top_n;
    if (Array.isArray(v2Items)) return v2Items.length;
    const legacy = dashboardData?.disclosure_check;
    return Array.isArray(legacy) ? legacy.length : 0;
};

const buildConsultationBriefing = (dashboardData) => {
    const levelCounts = countTrendLevels(dashboardData?.health_trend_v2);
    const grade = dashboardData?.health_grade_v2?.grade?.value || dashboardData?.sales_trigger?.health_grade || '-';
    const topPercentRaw = dashboardData?.health_grade_v2?.grade?.percentile_estimate;
    const topPercent = topPercentRaw ? Math.round(Number(topPercentRaw) * 100) : dashboardData?.sales_trigger?.top_percent;
    const timelineCount = Array.isArray(dashboardData?.timeline) ? dashboardData.timeline.length : 0;
    const precheckCount = getFiveYearPrecheckCount(dashboardData);
    const hasRisk = levelCounts.danger > 0 || levelCounts.caution > 0 || precheckCount > 0;

    return {
        statusTitle: hasRisk ? '주의 지표를 먼저 확인하세요' : '현재 상태는 안정적으로 보입니다',
        statusDesc: `위험 ${levelCounts.danger}개, 주의 ${levelCounts.caution}개, 정상 ${levelCounts.normal}개 지표가 확인됩니다.`,
        priorityTitle: precheckCount > 0 ? '고지/복용 이력 질문 우선' : '최근 변화 여부 질문 우선',
        priorityDesc: precheckCount > 0
            ? `최근 5년 점검 항목 ${precheckCount}건을 상담 전에 확인하세요.`
            : `최근 방문/검진 이력 ${timelineCount}건을 기준으로 변화 여부를 확인하세요.`,
        actionTitle: grade !== '-' ? `건강 등급 ${grade}등급` : '등급 산출 전',
        actionDesc: topPercent ? `상위 ${topPercent}% 추정. 할인/연계 설명 전에 근거 지표를 먼저 확인하세요.` : '리포트 근거를 확인한 뒤 다음 질문으로 연결하세요.',
    };
};

const ConsultationDashboardPage = () => {
    const [searchParams] = useSearchParams();
    const selectedPid = searchParams.get('std_patient_id') || '';
    const rightColumnRef = useRef(null);
    const [timelineHeight, setTimelineHeight] = useState(null);

    // [변경 사유]
    // - 상담 대시보드 URL에서 user_no 노출을 제거하기 위해, userNo는 라우트 파라미터로 받지 않습니다.
    // - 토큰 payload(sub) 또는 localStorage(user_no) 기반으로 로그인 사용자 userNo를 내부에서 획득합니다.
    const userNo = getUserNoFromToken() || window.localStorage.getItem('user_no') || '';

    const { data: dashboardData, isLoading, error } = useQuery(
        ['consultation', userNo, selectedPid],
        async () => {
            // [변경 사유]
            // - API URL 하드코딩을 제거하고 REACT_APP_API_URL 기반으로 호출합니다.
            // - 빌드/배포 환경에서 env 주입이 누락될 수 있어, 개발 기본값(localhost:8000)으로 폴백합니다.
            const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\/+$/, '');
            const pidQuery = selectedPid ? `&std_patient_id=${encodeURIComponent(selectedPid)}` : '';
            const response = await axios.get(`${apiBase}/api/v1/consultation/${userNo}?t=${new Date().getTime()}${pidQuery}`);
            console.log("✅ Consultation API Response:", response.data); // User requested logging
            return response.data;
        },
        {
            refetchOnWindowFocus: true,
            retry: 1,
        }
    );

    useEffect(() => {
        const target = rightColumnRef.current;
        if (!target || typeof ResizeObserver === 'undefined') return undefined;

        const syncTimelineHeight = () => {
            const nextHeight = Math.ceil(target.getBoundingClientRect().height);
            if (nextHeight <= 0) return;
            setTimelineHeight((prevHeight) => (
                Math.abs((prevHeight || 0) - nextHeight) > 1 ? nextHeight : prevHeight
            ));
        };

        syncTimelineHeight();
        const observer = new ResizeObserver(syncTimelineHeight);
        observer.observe(target);
        window.addEventListener('resize', syncTimelineHeight);

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', syncTimelineHeight);
        };
    }, [dashboardData]);

    if (isLoading) return <LoadingState>데이터를 불러오는 중입니다...</LoadingState>;
    if (!userNo) return <ErrorState>로그인 사용자 정보(user_no)를 확인할 수 없습니다.</ErrorState>;
    if (error) return <ErrorState>데이터 로딩 중 오류가 발생했습니다.</ErrorState>;

    const briefing = buildConsultationBriefing(dashboardData || {});

    return (
        <DashboardContainer>
            <BriefingSection>
                <BriefingLead>
                    <Eyebrow>30초 상담 브리핑</Eyebrow>
                    <BriefingTitle>고객 앞에서 바로 설명할 핵심만 먼저 확인합니다.</BriefingTitle>
                    <BriefingText>
                        건강 지표, 고지/복용 이력, 등급 가능성을 한 번에 묶어 상담 시작 질문과 후속 안내로 연결합니다.
                    </BriefingText>
                </BriefingLead>
                <BriefingGrid>
                    <BriefingCard bg="#fffaf0" border="#fbd38d">
                        <BriefingCardLabel>고객 상태 요약</BriefingCardLabel>
                        <BriefingCardTitle>{briefing.statusTitle}</BriefingCardTitle>
                        <BriefingCardDesc>{briefing.statusDesc}</BriefingCardDesc>
                    </BriefingCard>
                    <BriefingCard bg="#f0f9ff" border="#90cdf4">
                        <BriefingCardLabel>우선 상담 포인트</BriefingCardLabel>
                        <BriefingCardTitle>{briefing.priorityTitle}</BriefingCardTitle>
                        <BriefingCardDesc>{briefing.priorityDesc}</BriefingCardDesc>
                    </BriefingCard>
                    <BriefingCard bg="#f0fff4" border="#9ae6b4">
                        <BriefingCardLabel>후속 안내</BriefingCardLabel>
                        <BriefingCardTitle>{briefing.actionTitle}</BriefingCardTitle>
                        <BriefingCardDesc>{briefing.actionDesc}</BriefingCardDesc>
                    </BriefingCard>
                </BriefingGrid>
            </BriefingSection>

            {/* 1. 인사이트 (Trend) - Full Width */}
            <FullWidthSection>
                <Title>📈 주요 건강 지표 추세 (Trend)</Title>
                <div>
                    <HealthTrendChart data={dashboardData.health_trend} trendV2={dashboardData.health_trend_v2} />
                </div>
            </FullWidthSection>

            <FullWidthSection>
                <Title>🧭 상담 진행 가이드</Title>
                <ActionList>
                    <ActionCard>
                        <ActionTitle>1. 지금 가장 불편한 변화 확인</ActionTitle>
                        <ActionDesc>검진 수치만 설명하지 말고 최근 증상, 생활 변화, 복용 변화부터 질문합니다.</ActionDesc>
                    </ActionCard>
                    <ActionCard>
                        <ActionTitle>2. 리포트 근거로 설명</ActionTitle>
                        <ActionDesc>아래 추세와 타임라인에서 근거를 확인한 뒤 고객이 이해할 수 있는 문장으로 풀어줍니다.</ActionDesc>
                    </ActionCard>
                    <ActionCard>
                        <ActionTitle>3. 후속 행동 연결</ActionTitle>
                        <ActionDesc>필요하면 상담 채팅, 건강 리포트, 마이데이터 재연결, 의료진 상담 권고로 이어갑니다.</ActionDesc>
                    </ActionCard>
                </ActionList>
            </FullWidthSection>

            <BottomRow>
                {/* 2. 라포 형성 (Timeline) - Left Column */}
                <LeftColumn>
                    {/* Timeline은 방문 이력이 길어질 수 있어 Health Grade 바닥선에 맞춘 내부 스크롤 영역으로 처리합니다. */}
                    <TimelineSection $height={timelineHeight}>
                        <Title>📋 병원 방문 이력 (Timeline)</Title>
                        <TimelineBody>
                            <VerticalHealthTimeline data={dashboardData.timeline} />
                        </TimelineBody>
                    </TimelineSection>
                </LeftColumn>

                {/* 3. 우측 패널: Pre-Check / Screening / Health Grade */}
                <RightColumn ref={rightColumnRef}>
                    {/* [변경 사유]
                     * - Screening Logic 영역이 축소되므로, Pre-Check 영역을 확장해
                     *   상세 리스트가 최소 3개 이상 안정적으로 노출되도록 합니다.
                     */}
                    <StretchSection>
                        <Title>✅ 고지의무 사전 점검 (Pre-Check)</Title>
                        <SectionBody>
                            {/* [변경 사유]
                             * - 고지의무 사전 점검 v2(disclosure_check_v2)가 있으면 v2 우선 렌더링합니다.
                             * - v2가 없으면 기존(disclosure_check) 데이터로 폴백합니다.
                             */}
                            <PreCheckPanel data={dashboardData.disclosure_check_v2 || dashboardData.disclosure_check} />
                        </SectionBody>
                    </StretchSection>

                    {/* [변경 사유]
                     * - 화면을 축소(낮은 높이)했을 때, Health Grade 카드에서 "보험사별 예상 할인" 타이틀이
                     *   스크롤 없이 보이도록 섹션 높이를 확장합니다.
                     * - 우측 섹션 총 높이가 커지므로, 좌측 Timeline 높이도 함께 확장(상단 주석 참조)합니다.
                     */}
                    <StretchSection>
                        <Title>🏅 예상 건강 등급 (Health Grade)</Title>
                        <SectionBody>
                            {/* [변경 사유]
                             * - health_grade_v2가 API 응답에 포함되는 경우, 프론트에서도 v2를 우선 표시해야
                             *   등급(1~4), 보험사별 할인 예측, 근거(evidence)가 화면에 반영됩니다.
                             * - v2가 아직 없는 환경/데이터에서는 기존 sales_trigger로 폴백합니다.
                             */}
                            <HealthGradeSummary data={dashboardData.health_grade_v2 || dashboardData.sales_trigger} />
                        </SectionBody>
                    </StretchSection>
                </RightColumn>
            </BottomRow>
        </DashboardContainer>
    );
};

export default ConsultationDashboardPage;
